/* eslint-disable @typescript-eslint/no-empty-function */
import { EWorldMapSetType } from '__generated__/globalTypes';
import {
    EventEntityCategory,
    EventEntityDefinition,
    EventEntityItem,
    EventEntityMultifilter,
    EventProgramItem,
    EventViewData,
    informationListItem
} from 'components/eventView/pages/eventData';
import dayjs from 'dayjs';
import { GuideQueryView_guideView_forms } from 'graphql/query/__generated__/GuideQueryView';
import { WorldMapStories_publishedStoriesForWorldMap } from 'graphql/query/__generated__/WorldMapStories';
import { WorldMapView_worldMapView } from 'graphql/query/__generated__/worldMapView';
import { getZoomPriorityMarker } from 'hooks/map/useZoomToMarker';
import { normalizeStringForSearch } from 'libs/utils/text/normalizeStringForSearch';
import { chain, intersection, sortBy, uniq, uniqBy } from 'lodash';
import { createContext, useEffect, useState } from 'react';
import { Localization } from 'static/localization/localization';
import { v4 as uuid } from 'uuid';

type EventProgramItemData = EventProgramItem & {
    dateKey: string;
    normalizedTitle: string;
    normalizedDescription: string;
    storyIdsToOpen: string[];
};

export interface ICtxEventView {
    eventData: EventViewData;
    categories: EventEntityCategory[];
    allStories: EventEntityItem[];
    entityStates: Record<string, EntityState>;
    mapEntityState: EntityState;
    mapCategories: EventEntityCategory[];
    setVisibleFloorIds: React.Dispatch<React.SetStateAction<string[] | null>>;
    visibleFloorIds: string[] | null;
    informationList: informationListItem[];
    allPrograms: EventProgramItemData[];
    visiblePrograms: EventProgramItemData[];
    forms: GuideQueryView_guideView_forms[];
    programCategoryIdsFilter: string[];
    programDayFilter: string;
    programSearchTerm: string;
    setProgramSearchTerm: React.Dispatch<React.SetStateAction<string | null>>;
    otherEntityDefinitions: EventEntityDefinition[];
    setProgramCategoryIdsFilter: React.Dispatch<React.SetStateAction<string[] | null>>;
    setProgramDayFilter: React.Dispatch<React.SetStateAction<string | null>>;
    isWide: boolean;
    isPreview: boolean;
    mobileMenuOpen: boolean;
    setMobileMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
    scrollToTop: boolean;
    setScrollToTop: React.Dispatch<React.SetStateAction<boolean>>;
    eventPath: string;
    programDays: {
        dateKey: string;
        day: number;
        dayName: string;
    }[];
    programTitleShort: string;
    isEmbed: boolean;
    shuffleHash: string;
    rawGraphQLData: {
        map: WorldMapView_worldMapView;
        stories: WorldMapStories_publishedStoriesForWorldMap[];
    };
}

interface Props {
    children: React.ReactNode;
    event: EventViewData;
    isWide: boolean;
    isEmbed: boolean;
    isPreview: boolean;
    stories: WorldMapStories_publishedStoriesForWorldMap[];
    worldMap: WorldMapView_worldMapView;
    forms: GuideQueryView_guideView_forms[];
    eventPath: string;
}

export const CtxEventView = createContext<ICtxEventView>(null);

function entityNameToLocalized(entityName: string) {
    if (entityName === '_companies') {
        return Localization.COMPANIES;
    } else if (entityName === '_places') {
        return Localization.PLACES;
    } else if (entityName === '_exhibitors') {
        return Localization.EXHIBITORS;
    } else if (entityName === '_explore') {
        return Localization.EXPLORE;
    }
    return entityName;
}

interface EntityState {
    all: EventEntityItem[];
    visible: EventEntityItem[];
    filterIds: string[];
    searchTerm: string;
    setFilterIds: (val: string[] | null) => void;
    setSearchTerm: (val: string | null) => void;
    multifiltersOpen: boolean;
    setMultifiltersOpen: (val: boolean) => void;
}

const useEntitiesState = (data: {
    allStories: EventEntityItem[];
    event: EventViewData;
    otherEntityDefinitions: EventEntityDefinition[];
}): Record<string, EntityState> => {
    const [searchTerm, setSearchTerm] = useState<Record<string, string | null>>({});
    const [filterIds, setFilterIds] = useState<Record<string, string[] | null>>({});
    const [multifiltersOpen, setMultifiltersOpen] = useState<boolean>(false);

    function getVisibleList(
        all: EventEntityItem[],
        ids: string[] | null,
        term: string,
        multifilter?: EventEntityMultifilter[]
    ) {
        const selectedPerFilter = multifilter?.map((filter) => {
            return filter.optionIds.map((e) => e.categoryId).filter((e) => ids?.includes(e));
        });

        return all.filter((entity) => {
            if (multifilter && ids) {
                for (let i = 0; i < multifilter.length; i++) {
                    const selectedIds = selectedPerFilter[i];
                    const idsPresent = intersection(
                        entity.categoryIds,
                        multifilter[i].optionIds.map((e) => e.categoryId)
                    );
                    if (
                        selectedIds.length &&
                        idsPresent.length &&
                        !intersection(selectedIds, entity.categoryIds).length
                    ) {
                        return false;
                    }
                }
            }

            if (ids && !intersection(ids, entity.categoryIds).length) {
                return false;
            }

            if (term?.trim()) {
                const termNormalized = normalizeStringForSearch(term);
                const titleNormalized = normalizeStringForSearch(entity.storyHeader?.title);

                return titleNormalized.includes(termNormalized);
            }
            return true;
        });
    }

    const allMapStories = data.otherEntityDefinitions
        .filter((e) => e.isMapEntity)
        .flatMap((e) => e.list ?? []);
    const multipleMapEntitesMapState: EntityState = {
        all: allMapStories,
        visible: getVisibleList(allMapStories, filterIds['map'], searchTerm['map']),
        filterIds: filterIds['map'],
        searchTerm: searchTerm['map'],
        setFilterIds: (ids) => setFilterIds((curr) => ({ ...curr, map: ids })),
        setSearchTerm: (val) => setSearchTerm((curr) => ({ ...curr, map: val })),
        multifiltersOpen,
        setMultifiltersOpen
    };

    const otherEntities: [string, EntityState][] = data.otherEntityDefinitions.map((entity) => [
        entity.id,
        {
            all: entity.list ?? [],
            visible: getVisibleList(
                entity.list,
                filterIds[entity.id],
                entity.searchable ? searchTerm[entity.id] : '',
                entity.multifilters
            ),
            filterIds: filterIds[entity.id],
            searchTerm: searchTerm[entity.id],
            setFilterIds: (ids) => setFilterIds((curr) => ({ ...curr, [entity.id]: ids })),
            setSearchTerm: (val) => setSearchTerm((curr) => ({ ...curr, [entity.id]: val })),
            multifiltersOpen,
            setMultifiltersOpen
        }
    ]);

    return Object.fromEntries([['map', multipleMapEntitesMapState], ...otherEntities]);
};

export const CtxEventViewProvider = ({
    children,
    event,
    isWide,
    isPreview,
    stories,
    worldMap,
    eventPath,
    forms,
    isEmbed
}: Props) => {
    const eventData = event;

    const [visibleFloorIds, setVisibleFloorIds] = useState<string[] | null>(null);
    const [programCategoryIdsFilter, setProgramCategoryIdsFilter] = useState<string[] | null>(null);

    const [programDayFilter, setProgramDayFilter] = useState<string | null>(null);
    const [programSearchTerm, setProgramSearchTerm] = useState<string | null>(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [scrollToTop, setScrollToTop] = useState(false);
    const [shuffleHash] = useState(uuid().replace(/-/g, '').toLowerCase());

    const _categories =
        worldMap?.worldMapSets
            ?.filter((e) => e.type === EWorldMapSetType.CATEGORY)
            ?.map<EventEntityCategory>((e) => ({
                categoryId: e.worldMapSetId,
                title: e.title,
                orderIndex: e.orderIndex
            })) ?? [];
    const categories = sortBy(_categories, (e) => e.orderIndex);

    const allStories = (stories ?? []).map<EventEntityItem>((story) => {
        const connectedMarker = getZoomPriorityMarker(story.connectedMarkers);

        return {
            storyId: story.storyId,
            title: story.header?.title,
            imageUrl: story.header?.imageUrl,
            markerId: connectedMarker?.worldMapMarkerId,
            storyHeader: story.header,
            storyCards: story.cards,
            categoryIds: story.categories?.map((e) => e.worldMapSetId) ?? [],
            isHighlighted: false
        };
    });

    const otherEntityDefinitions = (
        eventData.eventInfo?.otherEntities ?? []
    ).map<EventEntityDefinition>((entityDefinition) => {
        const allCategories =
            entityDefinition?.categoryIds
                ?.map<EventEntityCategory>((e) => categories.find((c) => c.categoryId === e))
                .filter(Boolean) ?? [];
        function getCategories() {
            const customFilterForCategories = eventData.filterSettings?.[entityDefinition.id];

            return customFilterForCategories?.type === 'simple'
                ? allCategories.filter((e) =>
                      customFilterForCategories.optionIds.includes(e.categoryId)
                  )
                : allCategories;
        }

        function getMultiFilters() {
            const filters = eventData.filterSettings?.[entityDefinition.id];
            if (!filters || filters.type !== 'complex') {
                return null;
            }

            return filters.filters.map((filter) => ({
                filterId: filter.filterId,
                title: filter.title,
                optionIds: allCategories.filter((e) => filter.optionIds.includes(e.categoryId))
            }));
        }

        return {
            ...entityDefinition,
            name: entityNameToLocalized(entityDefinition.name),
            list: entityDefinition.listIds
                .map((e) => allStories.find((s) => s.storyId === e))
                .filter(Boolean),
            categories: getCategories(),
            multifilters: getMultiFilters(),
            isMapEntity: entityDefinition.isMapEntity ?? false,
            searchable: entityDefinition.searchable ?? false
        };
    });

    const entityStates = useEntitiesState({
        allStories,
        otherEntityDefinitions,
        event: eventData
    });

    const customFilterMapCategories = eventData.filterSettings?.['map'];
    const mapEntityDefinitions = otherEntityDefinitions.filter((e) => e.isMapEntity);
    const mapEntityState =
        mapEntityDefinitions?.length === 1 && !customFilterMapCategories
            ? entityStates[mapEntityDefinitions[0].id]
            : entityStates['map'];

    const getMapCategories = () => {
        if (mapEntityDefinitions.length > 0) {
            if (mapEntityDefinitions.length === 1) {
                return mapEntityDefinitions[0].categories;
            }
            return uniqBy(
                mapEntityDefinitions.flatMap((e) => e.categories ?? []),
                (e) => e.categoryId
            );
        }
        const allUsedCategoryIds = uniq(
            allStories.flatMap((e) => (e.markerId ? e.categoryIds : []))
        );

        return categories.filter((e) => allUsedCategoryIds.includes(e.categoryId));
    };
    const _mapCategories = getMapCategories();
    const mapCategories =
        customFilterMapCategories && customFilterMapCategories.type === 'simple'
            ? _mapCategories.filter((e) =>
                  customFilterMapCategories.optionIds.includes(e.categoryId)
              )
            : _mapCategories;

    const informationList =
        eventData.eventInfo?.information?.listIds
            ?.map((id) => stories.find((e) => e.storyId === id))
            ?.filter(Boolean)
            ?.map<informationListItem>((e) => ({
                storyId: e.storyId,
                title: e.header.title,
                description: e.header.text,
                storyCards: e.cards
            })) ?? [];

    const speakersList = eventData.programSettings?.speakersListId
        ? otherEntityDefinitions.find((e) => e.id === eventData.programSettings?.speakersListId)
        : null;

    const allPrograms = eventData.eventInfo?.program?.items.map<EventProgramItemData>((item) => {
        const dateExtension = eventData.eventInfo?.program?.dateExtensionHours ?? 0;

        const originalDate = new Date(item.startDateTime);
        const shiftedDate = new Date(item.startDateTime);
        shiftedDate.setUTCHours(shiftedDate.getUTCHours() - dateExtension);

        const isSameDayWhenShifted = shiftedDate.getUTCDate() === originalDate.getUTCDate();

        const getStory = () => {
            const storyById = stories?.find((e) => e.storyId === item.storyId);
            if (storyById) {
                return storyById;
            }
            if (item.markerId && item.autoStoryId) {
                const storyByMarker = stories?.find((e) =>
                    e.connectedMarkers.some((m) => m?.worldMapMarkerId === item.markerId)
                );
                if (storyByMarker) {
                    return storyByMarker;
                }
            }
            return null;
        };

        const story = getStory();

        const storyIds = (item.storyId ? [item.storyId] : item.storyIds) ?? [];
        const title = item.title ?? story?.header?.title ?? '';
        const description = item.description ?? story?.header?.text ?? '';

        const getStoryIdsToOpen = () => {
            if (!speakersList) {
                return storyIds;
            }

            const storyIdsWithoutSpeakers = storyIds.filter(
                (storyId) => !speakersList.list.some((e) => e.storyId === storyId)
            );
            if (!storyIdsWithoutSpeakers?.length) {
                return storyIds;
            }

            return storyIdsWithoutSpeakers;
        };

        return {
            ...item,
            title,
            description,
            normalizedTitle: normalizeStringForSearch(title),
            normalizedDescription: normalizeStringForSearch(description),
            storyId: story?.storyId ?? null,
            storyIds: storyIds,
            storyIdsToOpen: getStoryIdsToOpen(),
            dateKey: isSameDayWhenShifted
                ? originalDate.toISOString().substring(0, 10)
                : shiftedDate.toISOString().substring(0, 10)
        };
    });

    const programDays = chain(allPrograms)
        .map((e) => e.dateKey)
        .uniqBy((e) => e)
        .orderBy((e) => e)
        .map((e) => ({
            dateKey: e,
            day: new Date(e).getUTCDate(),
            dayName: dayjs.utc(e).format('ddd')
        }))
        .value();

    const searchTermNormalized = normalizeStringForSearch(programSearchTerm);
    const visiblePrograms = allPrograms?.filter((program) => {
        if (searchTermNormalized) {
            return (
                program.normalizedTitle.includes(searchTermNormalized) ||
                program.normalizedDescription.includes(searchTermNormalized)
            );
        }

        if (
            programCategoryIdsFilter &&
            !programCategoryIdsFilter.some((categoryId) => program.categoryIds.includes(categoryId))
        ) {
            return false;
        }

        if (programDayFilter) {
            return programDayFilter.includes(program.dateKey);
        }
        return true;
    });

    // Reset filter for programs. This is needed for preview
    // and should not be used in view because there is never 0 visible programs
    useEffect(() => {
        if (visiblePrograms.length === 0 && allPrograms.length) {
            setProgramDayFilter(allPrograms[0].dateKey);
        }
    }, [visiblePrograms.length, allPrograms.length]);

    useEffect(() => {
        if (!programDayFilter) {
            const today = dayjs.utc().format('YYYY-MM-DD');
            if (programDays.some((e) => e.dateKey === today)) {
                setProgramDayFilter(today);
            } else {
                setProgramDayFilter(programDays[0]?.dateKey ?? null);
            }
        }
    }, [programDayFilter]);

    useEffect(() => {
        const floors = worldMap?.worldMapSets?.filter(
            (e) => e.type === EWorldMapSetType.FLOOR && e.isVisibleOnLoad
        );
        if (floors?.length > 0) {
            setVisibleFloorIds(floors.map((e) => e.worldMapSetId));
        }
    }, []);

    return (
        <CtxEventView.Provider
            value={{
                rawGraphQLData: {
                    map: worldMap,
                    stories
                },
                eventData,
                categories,
                allStories,
                entityStates,
                mapEntityState,
                mapCategories,
                informationList,
                allPrograms,
                visiblePrograms,
                setProgramDayFilter,
                setProgramCategoryIdsFilter,
                programCategoryIdsFilter,
                programDayFilter,
                visibleFloorIds,
                setVisibleFloorIds,
                otherEntityDefinitions,
                isWide,
                isPreview,
                mobileMenuOpen,
                setMobileMenuOpen,
                scrollToTop,
                setScrollToTop,
                eventPath,
                programSearchTerm,
                setProgramSearchTerm,
                programDays,
                isEmbed,
                forms,
                shuffleHash,
                programTitleShort:
                    eventData.programSettings?.programTitle ?? Localization.PROGRAMME_SHORT
            }}
        >
            {children}
        </CtxEventView.Provider>
    );
};
